// /admin/ads → "וואטסאפ": everyone who wrote to FBZipper's own WhatsApp number (Cloud API),
// the ad that brought them, their account, and the thread with a reply box. Free-text
// replies only work within 24h of the person's last message (WhatsApp rule).
import {FC, useCallback, useEffect, useRef, useState} from 'react'
import {getWaContacts, getWaMedia, getWaMessages, sendWaReply, transcribeWa, WaContact, WaMessage} from './_requests'

const errMsg = (e: any) => e?.response?.data?.error || e?.message || 'שגיאה'
const when = (iso?: string) =>
  iso ? new Date(iso).toLocaleString('he-IL', {dateStyle: 'short', timeStyle: 'short'}) : ''
const phone = (waId: string) => (waId.startsWith('972') ? '0' + waId.slice(3) : '+' + waId)

const isVoice = (m: WaMessage) => m.direction === 'in' && (m.type === 'audio' || m.type === 'voice')
const errText = (e: any) => {
  // Blob responses carry the JSON error as a blob.
  const d = e?.response?.data
  return (d && typeof d === 'object' && 'error' in d && d.error) || e?.message || 'שגיאה'
}

// Voice note: ▶️ loads the audio (Meta keeps it ~30 days) and plays it; the transcript shows
// under it (new notes are transcribed on arrival, older ones with the 📝 button).
const VoiceNote: FC<{m: WaMessage}> = ({m}) => {
  const [src, setSrc] = useState('')
  const [loading, setLoading] = useState(false)
  const [transcript, setTranscript] = useState(m.transcript || '')
  const [tBusy, setTBusy] = useState(false)
  const [err, setErr] = useState('')
  useEffect(() => {
    if (m.transcript) setTranscript(m.transcript)
  }, [m.transcript])
  useEffect(() => () => {
    if (src) URL.revokeObjectURL(src)
  }, [src])
  const play = async () => {
    if (!m.message_id) return
    setLoading(true)
    setErr('')
    try {
      const {data} = await getWaMedia(m.message_id)
      if (data.type && data.type.includes('json')) throw new Error(JSON.parse(await data.text()).error)
      setSrc(URL.createObjectURL(data))
    } catch (e: any) {
      let msg = errText(e)
      if (e?.response?.data instanceof Blob) {
        try {
          msg = JSON.parse(await e.response.data.text()).error || msg
        } catch {
          msg = 'השמע לא זמין (וואטסאפ שומר כ-30 יום)'
        }
      }
      setErr(msg)
    } finally {
      setLoading(false)
    }
  }
  const doTranscribe = async () => {
    if (!m.message_id) return
    setTBusy(true)
    setErr('')
    try {
      const {data} = await transcribeWa(m.message_id)
      setTranscript(data.transcript)
    } catch (e) {
      setErr(errText(e))
    } finally {
      setTBusy(false)
    }
  }
  return (
    <div>
      <div className='fs-7 mb-1'>🎤 הודעה קולית</div>
      {src ? (
        <audio controls autoPlay src={src} style={{maxWidth: 260, height: 36}} />
      ) : (
        <button className='btn btn-sm btn-light py-1' onClick={play} disabled={loading || !m.message_id}>
          {loading ? 'טוען…' : '▶️ השמעה'}
        </button>
      )}
      {transcript ? (
        <div className='fs-7 mt-2 p-2 rounded' style={{background: '#f3f6f9', fontStyle: 'italic'}}>
          📝 {transcript}
        </div>
      ) : (
        <button className='btn btn-sm btn-link py-1 ms-2' onClick={doTranscribe} disabled={tBusy || !m.message_id}>
          {tBusy ? 'מתמלל…' : '📝 תמלול'}
        </button>
      )}
      {err && <div className='text-danger fs-8 mt-1'>{err}</div>}
    </div>
  )
}

const BOT_STATE: Record<string, string> = {
  ask_name: 'בוט: מחכה לשם',
  ask_business: 'בוט: מחכה לתחום',
  creating: 'בוט: פותח חשבון',
  done: 'בוט: חשבון נפתח',
  handoff: 'נציג מטפל',
}

const WaInbox: FC = () => {
  const [contacts, setContacts] = useState<(WaContact & {bot_state?: string})[]>([])
  const [q, setQ] = useState('')
  const [fromAds, setFromAds] = useState(false)
  const [sel, setSel] = useState<string | null>(null)
  const [msgs, setMsgs] = useState<WaMessage[]>([])
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const bottom = useRef<HTMLDivElement>(null)
  const thread = useRef<HTMLDivElement>(null)
  const shown = useRef(0) // messages in the open thread, to notice new ones

  const loadContacts = useCallback(async () => {
    try {
      const {data} = await getWaContacts(q, fromAds)
      setContacts(data.contacts)
    } catch (e) {
      setError(errMsg(e))
    }
  }, [q, fromAds])

  // force: opening a thread / after sending → jump to the newest message. The 10s poll only
  // follows new messages when the reader is already at the bottom, and never scrolls the page
  // (scrolls the thread box itself, not scrollIntoView), so reading older messages isn't yanked away.
  const loadThread = useCallback(async (waId: string, force?: boolean) => {
    const box = thread.current
    const atBottom = !box || box.scrollHeight - box.scrollTop - box.clientHeight < 60
    const {data} = await getWaMessages(waId)
    const grew = data.messages.length > shown.current
    shown.current = data.messages.length
    setMsgs(data.messages) // statuses (delivered/read) refresh too; only new messages scroll
    if (force || (grew && atBottom)) {
      setTimeout(() => {
        const b = thread.current
        if (b) b.scrollTop = b.scrollHeight
      }, 50)
    }
  }, [])

  useEffect(() => {
    loadContacts()
  }, [loadContacts])

  // Light polling so new messages show up without a refresh.
  useEffect(() => {
    const t = setInterval(() => {
      loadContacts()
      if (sel) loadThread(sel).catch(() => {})
    }, 10000)
    return () => clearInterval(t)
  }, [loadContacts, loadThread, sel])

  const open = async (waId: string) => {
    setSel(waId)
    setError('')
    try {
      await loadThread(waId, true)
      loadContacts()
    } catch (e) {
      setError(errMsg(e))
    }
  }

  const send = async () => {
    if (!sel || !text.trim()) return
    setBusy(true)
    setError('')
    try {
      await sendWaReply(sel, text.trim())
      setText('')
      await loadThread(sel, true)
      loadContacts()
    } catch (e) {
      setError(errMsg(e))
    } finally {
      setBusy(false)
    }
  }

  const c = contacts.find((x) => x.wa_id === sel)

  return (
    <div>
      <div className='d-flex gap-2 align-items-center flex-wrap mb-3'>
        <input
          className='form-control form-control-sm'
          style={{maxWidth: 260}}
          placeholder='חיפוש שם / טלפון / הודעה…'
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className='form-check form-switch'>
          <input
            className='form-check-input'
            type='checkbox'
            id='wa-from-ads'
            checked={fromAds}
            onChange={(e) => setFromAds(e.target.checked)}
          />
          <label className='form-check-label fs-7' htmlFor='wa-from-ads'>
            רק מי שהגיע ממודעה
          </label>
        </div>
        <span className='text-muted fs-8 ms-auto'>
          המספר: 054-615-5952 · {contacts.length} אנשי קשר
        </span>
      </div>
      {error && <div className='alert alert-danger py-2 fs-7'>{error}</div>}

      <div className='d-flex gap-3 flex-wrap' style={{alignItems: 'stretch'}}>
        <div
          className='card card-bordered'
          style={{flex: '1 1 300px', maxWidth: 420, maxHeight: 640, overflowY: 'auto'}}
        >
          {contacts.length === 0 && <div className='p-5 text-muted'>עוד אף אחד לא כתב למספר.</div>}
          {contacts.map((x) => (
            <div
              key={x.wa_id}
              onClick={() => open(x.wa_id)}
              className='p-3 border-bottom'
              style={{cursor: 'pointer', background: x.wa_id === sel ? '#eef4ff' : undefined}}
            >
              <div className='d-flex justify-content-between'>
                <b>{x.name || phone(x.wa_id)}</b>
                <span className='text-muted fs-8'>{when(x.last_message_at)}</span>
              </div>
              <div className='d-flex justify-content-between align-items-center gap-2'>
                <span className='text-muted fs-7 text-truncate'>
                  {x.last_direction === 'out' ? 'אתם: ' : ''}
                  {x.last_text}
                </span>
                {x.unread > 0 && (
                  <span className='badge badge-circle badge-success'>{x.unread}</span>
                )}
              </div>
              <div className='d-flex gap-1 flex-wrap mt-1'>
                {x.ad_name && (
                  <span className='badge badge-light-primary fs-9'>📣 {x.ad_name}</span>
                )}
                {x.account_name || x.account ? (
                  <span className='badge badge-light-success fs-9'>
                    חשבון: {x.account_name || 'קיים'}
                  </span>
                ) : null}
                {x.bot_state && BOT_STATE[x.bot_state] && (
                  <span className='badge badge-light-info fs-9'>{BOT_STATE[x.bot_state]}</span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div
          className='card card-bordered d-flex flex-column'
          style={{flex: '2 1 380px', minHeight: 420, maxHeight: 640}}
        >
          {!c ? (
            <div className='p-5 text-muted'>בחרו שיחה מהרשימה.</div>
          ) : (
            <>
              <div className='p-3 border-bottom'>
                <div className='fw-bold'>
                  {c.name || phone(c.wa_id)}{' '}
                  <span className='text-muted fs-8' dir='ltr'>
                    {phone(c.wa_id)}
                  </span>
                </div>
                <div className='fs-8 text-muted'>
                  כתב לראשונה {when(c.first_message_at)}
                  {c.ad_name ? ` · הגיע מהמודעה: ${c.ad_name}` : ' · לא ממודעה'}
                  {c.account ? (
                    <>
                      {' · '}
                      <a href={`/admin/accounts/${c.account}`}>פתיחת החשבון</a>
                    </>
                  ) : (
                    ' · אין חשבון'
                  )}
                </div>
              </div>
              <div ref={thread} className='p-3 flex-grow-1' style={{overflowY: 'auto', background: '#f5f1ea'}}>
                {msgs.map((m) => (
                  <div
                    key={m._id}
                    className={`d-flex mb-2 ${
                      m.direction === 'out' ? 'justify-content-start' : 'justify-content-end'
                    }`}
                  >
                    <div
                      style={{
                        maxWidth: '75%',
                        whiteSpace: 'pre-wrap',
                        background: m.direction === 'out' ? '#d9fdd3' : '#fff',
                        borderRadius: 10,
                        padding: '6px 10px',
                        boxShadow: '0 1px 1px rgba(0,0,0,.08)',
                      }}
                    >
                      {isVoice(m) ? <VoiceNote m={m} /> : <div className='fs-7'>{m.text}</div>}
                      <div className='text-muted' style={{fontSize: 10}}>
                        {when(m.at)}
                        {m.direction === 'out' && m.status ? ` · ${m.status}` : ''}
                        {m.error ? ` · ${m.error}` : ''}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={bottom} />
              </div>
              <div className='p-3 border-top'>
                {!c.window_open && (
                  <div className='text-warning fs-8 mb-2'>
                    עברו יותר מ-24 שעות מההודעה האחרונה שלו — וואטסאפ יאפשר לשלוח רק תבנית מאושרת,
                    לא טקסט חופשי.
                  </div>
                )}
                <div className='d-flex gap-2'>
                  <textarea
                    className='form-control form-control-sm'
                    rows={2}
                    value={text}
                    placeholder='כתבו תשובה… (שליחה עוצרת את הבוט בשיחה הזו)'
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        send()
                      }
                    }}
                  />
                  <button
                    className='btn btn-sm btn-success'
                    disabled={busy || !text.trim()}
                    onClick={send}
                  >
                    שליחה
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default WaInbox
